import { AsyncStateNotification } from '@ui/notifications';
import { BulkEditModal } from '@ui/modals/bulk/BulkEditModal';
import { BulkEditModalSection } from '@ui/modals/bulk/BulkEditModalSection';
import { ClassificationBulkSelector } from '@ui/tags/ClassificationBulkSelector';
import { createSonarSelector } from '@lib/selectors';
import { difference } from 'lodash';
import { StakeholderSelector } from '@ui/StakeholderSelector';
import { UserSelector } from '@ui/UserSelector';

import {
	ClassificationLevel,
	coreActions,
	DataSecurityApplication,
	StakeholderType
} from '@sonar-software/sonar-core';
import { COPY_GENERIC, COPY_SPECIFIC, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import React, { useState } from 'react';
import { useAction, UseSelectionTableWithInversionResult } from '@hooks';

const asArray = (ids: number | Array<number>) => (Array.isArray(ids) ? ids : [ ids ]);
const bulkPatchSelector = createSonarSelector((state) => state.core.dataSecurity.applications.bulk.patch);

export interface AppTableBulkEditModalProps {
	popoverTarget: HTMLElement | null;
	selectableApps: DataSecurityApplication[];
	selectionData: UseSelectionTableWithInversionResult<DataSecurityApplication, number>;
	typeLabel: string;
}

export const AppTableBulkEditModal: React.FC<AppTableBulkEditModalProps> = ({
	popoverTarget,
	selectableApps,
	selectionData,
	typeLabel
}) => {
	const [ ownerId, setOwnerId ] = useState<number>();
	const [ stakeholderIds, setStakeholderIds ] = useState<number | number[]>();
	const [ stakeholderGroupId, setStakeholderGroupId ] = useState<number | number[]>();
	const [ classification, setClassification ] = useState<ClassificationLevel>();

	const bulkPatchApplications = useAction(coreActions.requestPatchCoreDataSecurityApplicationsBulk);

	const reset = () => {
		setOwnerId(undefined);
		setStakeholderIds(undefined);
		setStakeholderGroupId(undefined);
		setClassification(undefined);
		selectionData.resetSelection();
	};

	const disabled = !ownerId &&
		!stakeholderIds &&
		!stakeholderGroupId &&
		!classification;

	const handleOnOk = () => {
		bulkPatchApplications({
			applicationIds: selectionData.selectAllEnabled ?
				difference(selectableApps.map((app) => app.applicationId), selectionData.selectedKeys) :
				selectionData.selectedKeys,
			...classification ? {
				classification: classification === 'null' ? null : classification
			} : {},
			...ownerId ? { ownerId } : {},
			...stakeholderGroupId ? { stakeholderGroupId: Number(stakeholderGroupId) } : {},
			...stakeholderIds ? { stakeholderIds: asArray(stakeholderIds) } : {}
		});

		reset();
	};

	return (
		<>
			<BulkEditModal
				className='app-table-bulk-edit-modal'
				disabled={ disabled }
				onCancel={ reset }
				onOk={ handleOnOk }
				selectedCount={ selectionData.selectedCount }
				targetNode={ popoverTarget }
				typeLabel={ typeLabel }>
				<BulkEditModalSection title={ COPY_SPECIFIC.OWNER }>
					<UserSelector
						allowClear
						avatar
						onSingleChange={ setOwnerId }
						placeholder={ COPY_SPECIFIC.CHANGE_OWNER }
						value={ ownerId } />
				</BulkEditModalSection>
				<BulkEditModalSection title={ COPY_SPECIFIC.STAKEHOLDERS }>
					<StakeholderSelector
						avatar
						direction='horizontal'
						mode='multiple'
						onChange={ setStakeholderIds }
						placeholder={ COPY_SPECIFIC.ADD_STAKEHOLDERS }
						value={ stakeholderIds } />
				</BulkEditModalSection>
				<BulkEditModalSection title={ COPY_SPECIFIC.GROUP }>
					<StakeholderSelector
						avatar
						onChange={ setStakeholderGroupId }
						placeholder={ COPY_SPECIFIC.ADD_GROUP }
						stakeholderType={ StakeholderType.Team }
						value={ stakeholderGroupId } />
				</BulkEditModalSection>
				<BulkEditModalSection title={ COPY_SPECIFIC.CLASSIFICATION }>
					<ClassificationBulkSelector
						boxless={ false }
						onChange={ setClassification }
						value={ classification } />
				</BulkEditModalSection>
			</BulkEditModal>
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				received={ NOTIFICATIONS_COPY.SUCCESS_PATCH_APPLICATIONS_BULK }
				resetRawAction={ coreActions.resetPatchCoreDataSecurityApplicationsBulk }
				stateSelector={ bulkPatchSelector } />
		</>
	);
};
